import * as types from '@/store/mutation-types'
import { Repository } from '@/repository/repository'
import { stripPayload } from '@/schemas'
import { services } from '@/services'
import { saveAs } from 'file-saver'
import config from '../../../config'

const apiRepository = Repository.call('filters')

const _createReschedulingTree = (arr, childReason = false, idFather = null, levelFather = null) => {
  const out = arr.reduce((acc, item) => {
    const {
      description,
      id,
      level,
      requiresProofOfImage,
      type,
      reasonTypes,
      reasonSubTypes,
      reasons
    } = item

    const obj = {
      id: Date.now() + ((Math.random() * 100000).toFixed()),
      idChild: id,
      level,
      requiresProofOfImage,
      description,
      type
    }

    if (childReason) {
      obj.idFather = idFather
      obj.levelFather = levelFather
    }

    acc.push(obj)

    const arrReasons = [ reasonTypes, reasonSubTypes, reasons ]
    arrReasons.forEach(reason => {
      if (reason && reason.length > 0) {
        const childReasonSubTypes = _createReschedulingTree(reason, true, id, level)
        acc = [ ...acc, ...childReasonSubTypes ]
      }
    })

    return acc
  }, [])

  return out
}

const state = {
  orderedFilters: [],
  filter: {},
  moments: [],
  reschedules: [],
  schedules: [],
  stores: [],
  states: [],
  pods: []
}

const getters = {
  stores: state => state.stores,
  states: state => state.states,
  orderedFilters: state => state.orderedFilters,
  pods: state => state.pods,
  filtersSelected: state => state.filter,
  filtersIncidencesMoments: state => state.moments,
  filtersRechedules: state => state.reschedules,
  filtersSchedules: state => state.schedules,
  currentPage: state => state.filter.page + 1 || 1
}

const actions = {
  getFilters ({ commit }, type) {
    const typeFilter = (!type) ? 'sortOrders' : type
    const filters = stripPayload(config.orderFilters, typeFilter)
    commit(types.SET_ORDER_FILTERS, filters)
  },
  async getIncidentsFilters ({ commit }) {
    const incidents = await apiRepository.getIncidentReassons()
    if (!incidents.level) return incidents

    const response = services.cleanResponseService(incidents.data)
    const incidentsTreeForListView = _createReschedulingTree(response)
    commit(types.SET_INCIDENCES, incidentsTreeForListView)
  },
  async getReschedulesFilters ({ commit }) {
    const reschedule = await apiRepository.getRescheduleReassons()
    if (!reschedule.level) return reschedule

    const response = services.cleanResponseService(reschedule.data)
    const reschedulingTreeForListView = _createReschedulingTree(response)
    commit(types.SET_RESCHEDULES, reschedulingTreeForListView)
  },
  async getSchedulesFilters ({ commit }) {
    const schedules = await apiRepository.getSchedulesFilters()
    if (!schedules.level) return schedules

    const response = services.cleanResponseService(stripPayload(schedules.data, 'schedules'))
    commit(types.SET_SCHEDULES, response)
  },
  async getListPods ({ commit }, list) {
    const _getPOD = ({ id }) => apiRepository.getListPods(id)
    const _ext = base64 => base64.substring(base64.indexOf('/') + 1, base64.indexOf(';base64'))
    const _buildFilename = (index, customerOrderCode, ext) => `${customerOrderCode}_${index}.${ext}`
    const _getDataInfo = pod => {
      return {
        id: pod.id,
        customerOrderCode: pod.customerOrderCode,
        proofOfDelivery: pod.proofOfDelivery
      }
    }

    const listFiles = await Promise.all(list.map(item => _getPOD(item)))
      .then(data => data.map(d => d.data.map(pod => _getDataInfo(pod))))
      .then(async (arrPods) => {
        let listPodFiles = []
        arrPods.forEach(arrPod => {
          arrPod.forEach(async pod => {
            if (pod.proofOfDelivery !== null) {
              const fileName = _buildFilename(pod.id, pod.customerOrderCode, _ext(pod.proofOfDelivery))
              const blob = await fetch(pod.proofOfDelivery)
                .then(res => res.blob())
                .then(blob => blob)

              const out = {
                id: pod.id,
                fileName,
                blob
              }

              listPodFiles.push(out)
            }
          })
        })

        return listPodFiles
      })

    commit(types.SET_PODS, listFiles)
    return true
  },
  downloadPodFile  ({ commit }, { blob, fileName }) {
    saveAs(blob, fileName)
  },
  async deletePodFile  ({ state, commit }, id) {
    const deleteFile = await apiRepository.deletePodFile(id)
    if (!deleteFile.level) return deleteFile

    const newPodList = state.pods.filter(pod => pod.id !== id)
    commit(types.SET_PODS, newPodList)

    return deleteFile
  },
  async getStores ({ commit }) {
    const stores = await apiRepository.getStores()
    if (!stores.level) return stores

    const response = services.cleanResponseService(stripPayload(stores.data, 'storesFilters'))
    response.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))

    commit(types.SET_STORES_FILTERS, response)
  },
  async getOrderStates ({ commit }) {
    const states = await apiRepository.getStates()
    if (!states.level) return states

    commit(types.SET_STATES, states.data)
  },
  setStoreSelected ({ commit }, storeName) {
    commit(types.SET_SELECTED_STORE, storeName)
  },
  setOrderBoSelected ({ commit }, orderBo) {
    commit(types.SET_SELECTED_ORDERBO, orderBo)
  },
  setOrderState ({ commit }, orderState) {
    commit(types.SET_SELECTED_ORDERSTATE, orderState)
  },
  setDeliveryDateSelected ({ commit }, dates) {
    commit(types.SET_SELECTED_DELIVERYDATA, dates)
  },
  setOrderSelected ({ commit }, orderId) {
    commit(types.SET_SELECTED_ORDERID, orderId)
  },
  setOrd ({ commit }, ordOption) {
    commit(types.SET_SELECTED_ORD, ordOption)
  },
  flushUserFilters ({ commit }) {
    commit(types.SET_FLUSH_USER_FILTERS)
  },
  setPromisseDateSelected ({ commit }, dates) {
    commit(types.SET_SELECTED_PROMISSEDATA, dates)
  },
  setCollectionDateSelected ({ commit }, dates) {
    commit(types.SET_SELECTED_COLLECTIONDATA, dates)
  },
  flush ({ commit }) {
    commit(types.SET_FLUSH_FILTERS)
  },
  setSort ({ commit }, sortOption) {
    commit(types.SET_SELECTED_SORT, sortOption)
  },
  setPage ({ commit }, page) {
    // ! Buefy table starts page on 1
    const currPage = (page - 1 >= 0) ? page - 1 : 0
    commit(types.SET_SELECTED_PAGE, currPage)
  },
  resetPage ({ commit }) {
    commit(types.RESET_SELECTED_PAGE)
  }
}

const mutations = {
  [types.SET_SELECTED_SORT] (state, sortOption) {
    state.filter.sortOption = sortOption
  },
  [types.SET_SELECTED_PAGE] (state, page) {
    state.filter.page = page
  },
  [types.RESET_SELECTED_PAGE] (state) {
    state.filter.page = 0
  },
  [types.SET_SELECTED_ORDERID] (state, order) {
    state.filter.order = order
  },
  [types.SET_RESCHEDULES] (state, reschedules) {
    state.reschedules = reschedules
  },
  [types.SET_SCHEDULES] (state, schedules) {
    state.schedules = schedules
  },
  [types.SET_PODS] (state, pods) {
    state.pods = pods
  },
  [types.SET_ORDER_FILTERS] (state, filters) {
    state.orderedFilters = filters
  },
  [types.SET_INCIDENCES] (state, incidences) {
    state.moments = incidences
  },
  [types.SET_FLUSH_USER_FILTERS] (state) {
    state.filter = {}
  },
  [types.SET_SELECTED_DELIVERYDATA] (state, dates) {
    state.filter.deliveryDateBegin = dates.deliveryDateBegin
    state.filter.deliveryDateEnd = dates.deliveryDateEnd
  },
  [types.SET_STORES_FILTERS] (state, stores) {
    state.stores = stores
  },
  [types.SET_STATES] (state, states) {
    state.states = states
  },
  [types.SET_SELECTED_ORDERBO] (state, orderBo) {
    state.filter.orderBo = orderBo
  },
  [types.SET_SELECTED_ORDERSTATE] (state, orderState) {
    state.filter.orderState = orderState
  },
  [types.SET_SELECTED_PROMISSEDATA] (state, dates) {
    state.filter.promisseDateBegin = dates.promisseDateBegin
    state.filter.promisseDateEnd = dates.promisseDateEnd
  },
  [types.SET_SELECTED_COLLECTIONDATA] (state, dates) {
    state.filter.collectionDateBegin = dates.collectionDateBegin
    state.filter.collectionDateEnd = dates.collectionDateEnd
  },
  [types.SET_SELECTED_ORD] (state, ord) {
    state.filter.ord = ord
  },
  [types.SET_SELECTED_STORE] (state, storeName) {
    const store = storeName
      ? state.stores.find(store => store.name === storeName)
      : ''

    state.filter.store = store
  },
  [types.SET_FLUSH_FILTERS] (state) {
    state.orderedFilters = []
    state.filter = {}
    state.moments = []
    state.reschedules = []
    state.schedules = []
    state.stores = []
    state.states = []
    state.pods = []
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/filters.js